import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import MyEntries from './pages/MyEntries';
import SalesDashboard from './pages/SalesDashboard';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminTimesheets from './pages/admin/AdminTimesheets';
import AdminContractors from './pages/admin/AdminContractors';
import AdminReports from './pages/admin/AdminReports';
import AdminCommissions from './pages/admin/AdminCommissions';

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false, salesOnly = false }) => {
  const { user, effectiveUser, loading } = useAuth();
  const currentUser = effectiveUser || user;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse font-mono text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  if (salesOnly && currentUser?.contractor_type !== 'sales' && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Redirect authenticated users
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse font-mono text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (user) {
    if (user.role === 'admin') {
      return <Navigate to="/admin" replace />;
    } else if (user.contractor_type === 'sales') {
      return <Navigate to="/sales" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />

      {/* Contractor Routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/my-entries" 
        element={
          <ProtectedRoute>
            <MyEntries />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/sales" 
        element={
          <ProtectedRoute salesOnly>
            <SalesDashboard />
          </ProtectedRoute>
        } 
      />

      {/* Admin Routes */}
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute adminOnly>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/timesheets" 
        element={
          <ProtectedRoute adminOnly>
            <AdminTimesheets />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/contractors" 
        element={
          <ProtectedRoute adminOnly>
            <AdminContractors />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/reports" 
        element={
          <ProtectedRoute adminOnly>
            <AdminReports />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/commissions" 
        element={
          <ProtectedRoute adminOnly>
            <AdminCommissions />
          </ProtectedRoute>
        } 
      />

      {/* Default Redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
